import styled from '@emotion/styled';
import React, { useState } from 'react';
import { Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { Theme } from '../../../../../theme';
import { SelectSingle } from '../../Selects/SelectSingle';
import { FeedbackCard } from '../EmployeeFeedbackQuestion';
import { GoalLabel } from '../../../pages/PerformanceReview/Goals2/GoalStatusCard/GoalLabels/GoalLabel';
import { getStatusStr } from '../../../pages/PerformanceReview/Goals2/translations';
import type { GoalStatus } from '../../../pages/PerformanceReview/Goals2/types';
import type { GoalResultChange, InlineGoalObjective } from './types';

type Props = {
  objective: InlineGoalObjective;
  canEdit: boolean;
  onChange?: (change: GoalResultChange) => void;
};

const i18prefix = 'performanceReview.inlineGoalQuestion';

export const InlineGoalQuestion: React.FC<React.PropsWithChildren<Props>> = ({
  objective,
  canEdit,
  onChange,
}) => {
  const { t } = useTranslation();

  const predefinedOptions = objective.category?.predefinedResultOptions ?? [];
  const hasPredefinedOptions = predefinedOptions.length > 0;

  const [localValue, setLocalValue] = useState<string | null>(
    objective.predefinedResultOption?.Id ?? null,
  );
  const [numericValue, setNumericValue] = useState<string>(
    objective.displayResult ?? '',
  );

  const handlePredefinedChange = (optionId: string | null) => {
    setLocalValue(optionId);
    onChange?.({
      objectiveId: objective.Id,
      predefinedResultOptionId: optionId,
      result: null,
    });
  };

  const handleNumericBlur = () => {
    const parsed = numericValue !== '' ? parseFloat(numericValue) : null;
    onChange?.({
      objectiveId: objective.Id,
      predefinedResultOptionId: null,
      result: parsed,
    });
  };

  const selectOptions = predefinedOptions.map((opt) => ({
    value: opt.Id,
    label: opt.Name,
  }));

  const weight = objective.flair__Final_Weight__c ?? objective.flair__Weight__c;
  const categoryName = objective.category?.Name;
  const status = objective.flair__Current_Status__c as GoalStatus;

  return (
    <FeedbackCard>
      <div className="mb-3">
        <h3 className="mb-1">
          <span>{objective.Name}</span>
        </h3>
        <LabelsContainer>
          {weight != null && (
            <GoalLabel
              icon="phosphor-scales"
              label={`${weight}%`}
              className="me-3"
              noWrap={true}
            />
          )}
          {categoryName && (
            <GoalLabel
              icon="phosphor-stack"
              label={categoryName}
              className="me-3"
            />
          )}
          {status && (
            <GoalLabel
              icon="phosphor-pulse"
              label={getStatusStr(status)}
              className="me-3"
            />
          )}
          {objective.flair__Weighted_Achievement_Score__c != null && (
            <GoalLabel
              icon="chart-bar-outline"
              label={`${t('performanceReview.goals2.weightedScore.score')}: ${
                objective.flair__Weighted_Achievement_Score__c
              }`}
              className="me-3"
            />
          )}
          {objective.tags.map((tag) => (
            <GoalLabel
              key={tag.Id}
              icon="pricetag"
              label={tag.Name}
              className="me-3"
            />
          ))}
        </LabelsContainer>
      </div>

      {canEdit ? (
        <div style={{ maxWidth: '18.75rem' }}>
          {hasPredefinedOptions ? (
            <SelectSingle
              value={localValue}
              options={selectOptions}
              onChange={handlePredefinedChange}
              heightMode="small"
              placeholder={t(`${i18prefix}.resultPlaceholder`)}
            />
          ) : (
            <Form.Control
              type="number"
              size="sm"
              value={numericValue}
              onChange={(e) => setNumericValue(e.target.value)}
              onBlur={handleNumericBlur}
              placeholder={t(`${i18prefix}.resultPlaceholder`)}
            />
          )}
        </div>
      ) : (
        objective.displayResult && (
          <span className="text-muted">{objective.displayResult}</span>
        )
      )}
    </FeedbackCard>
  );
};

const LabelsContainer = styled('div')({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  color: Theme.color.dark,
  fontSize: Theme.font.size.sm,
});
